import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';

// Settings → Shipping → "Packing order" (Fernando + Henrik 20.10; docs/GUIDED-PP.md "Packing order").
// A guided round's delivery notes print in the order the cart boxes empty. Backend: backend/pp_packorder.py
// (app_settings 'pp_pack_order'), read at every Start.

interface S { enabled: boolean; big_from: number; dig_depth: number }

const input = 'bg-[#0f1117] border border-gray-700 text-white rounded px-2 py-1 text-sm keep-width w-16 text-center';

export default function PackOrderCard() {
  const [s, setS] = useState<S | null>(null);
  const [dirty, setDirty] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    authFetch(`${API}/pp2/settings/pack-order`).then(r => r.ok ? r.json() : null)
      .then(d => d && setS(d.settings)).catch(() => {});
  }, []);
  if (!s) return null;

  const change = (patch: Partial<S>) => { setS({ ...s, ...patch }); setDirty(true); };
  const num = (v: string) => parseInt(v.replace(/\D/g, ''), 10) || 0;
  const save = async () => {
    const r = await authFetch(`${API}/pp2/settings/pack-order`, { method: 'PUT', body: JSON.stringify(s) });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { setMsg(d.detail || 'Could not save'); return; }
    setS(d.settings); setDirty(false); setMsg('✓ Saved'); setTimeout(() => setMsg(''), 2500);
  };

  return (
    <div className="bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4">
      <div className="text-white text-sm font-medium">Packing order of a guided round</div>
      <div className="text-gray-500 text-xs mt-0.5 mb-3">
        The delivery notes print in the order the cart boxes empty. Carriers come first, in the pickup order above. Inside
        each carrier: 1-piece orders whose piece is <b className="text-gray-300">on top</b> of its box, then orders whose
        pieces are all on top, then 1-piece orders near the top, then the rest, and <b className="text-gray-300">big orders
        last</b> (still before the next carrier). As orders are packed, what lay beneath comes on top. The WMS then moves
        orders around inside a carrier as long as that means fewer pieces to lift.
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-300 mb-2">
        <input type="checkbox" checked={s.enabled} onChange={e => change({ enabled: e.target.checked })} />
        Ticked = pack in reverse order of picking, as far as the carrier order allows (unticked = carrier, then the oldest first)
      </label>
      <div className={`space-y-2 mb-3 ${s.enabled ? '' : 'opacity-40'}`}>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
          A big order has
          <input value={String(s.big_from)} disabled={!s.enabled} onChange={e => change({ big_from: num(e.target.value) })} className={input} />
          pieces or more (2–50)
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-300">
          A 1-piece order counts as near the top down to the
          <input value={String(s.dig_depth)} disabled={!s.enabled} onChange={e => change({ dig_depth: num(e.target.value) })} className={input} />
          th piece from the top (1–20)
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={!dirty} className="px-3 py-1.5 rounded bg-blue-600 hover:bg-blue-500 text-white text-sm disabled:opacity-40">Save</button>
        {msg && <span className="text-sm text-green-400">{msg}</span>}
      </div>
    </div>
  );
}
